import React from 'react';
import { Button, Form, Input, Spin, Typography } from 'antd';
import { useMutation } from '@apollo/client';
// Api
import { GET_INFLUENCER_CATEGORIES } from 'api/influencerCategory/queries';
import {
  ADD_INFLUENCER_CATEGORY,
  UPDATE_INFLUENCER_CATEGORY,
} from 'api/influencerCategory/mutations';
// Types
import {
  AddInfluencerCategory,
  AddInfluencerCategoryVariables,
} from 'api/influencerCategory/types/AddInfluencerCategory';
import {
  UpdateInfluencerCategory,
  UpdateInfluencerCategoryVariables,
} from 'api/influencerCategory/types/UpdateInfluencerCategory';
import { GetInfluencerCategories_getInfluencerCategories } from 'api/influencerCategory/types/GetInfluencerCategories';
// UI
import { successNotification, errorNotification } from 'ui/Notification';

type FormValues = {
  name: string;
};

export type CreateEditInfluencerCategoryProps = {
  record: GetInfluencerCategories_getInfluencerCategories | null;
  onFinish: () => void;
};

const { Text } = Typography;

const CreateEditInfluencerCategory = ({
  record,
  onFinish,
}: CreateEditInfluencerCategoryProps): JSX.Element => {
  const isEdit = !!record?.id;

  const initialFormValues: FormValues = {
    name: record?.name || '',
  };

  const [addInfluencerCategory, { loading: addLoading }] = useMutation<
    AddInfluencerCategory,
    AddInfluencerCategoryVariables
  >(ADD_INFLUENCER_CATEGORY, {
    refetchQueries: [{ query: GET_INFLUENCER_CATEGORIES }],
  });

  const [updateInfluencerCategory, { loading: updateLoading }] = useMutation<
    UpdateInfluencerCategory,
    UpdateInfluencerCategoryVariables
  >(UPDATE_INFLUENCER_CATEGORY, {
    refetchQueries: [{ query: GET_INFLUENCER_CATEGORIES }],
  });

  const handleCreate = async (values: FormValues) => {
    try {
      await addInfluencerCategory({
        variables: {
          input: {
            name: values.name.trim(),
          },
        },
      });

      successNotification('Influencer category created');
      onFinish();
    } catch (error) {
      errorNotification((error as Error)?.message);
    }
  };

  const handleUpdate = async (values: FormValues) => {
    if (!record?.id) {
      return;
    }

    try {
      await updateInfluencerCategory({
        variables: {
          input: {
            id: record.id,
            name: values.name.trim(),
          },
        },
      });

      successNotification('Influencer category updated');
      onFinish();
    } catch (error) {
      errorNotification((error as Error)?.message);
    }
  };

  const handleSubmit = async (values: FormValues) => {
    if (isEdit) {
      await handleUpdate(values);
    } else {
      await handleCreate(values);
    }
  };

  const loading = addLoading || updateLoading;

  return (
    <Form
      layout="vertical"
      name="createEditInfluencerCategory"
      initialValues={initialFormValues}
      autoComplete="off"
      onFinish={handleSubmit}
    >
      <Form.Item
        name="name"
        label={<Text>Name</Text>}
        rules={[
          {
            required: true,
            message: 'Please enter the category name',
            whitespace: true,
          },
        ]}
      >
        <Input placeholder="e.g. Fitness, Gaming, Travel" />
      </Form.Item>

      {loading ? (
        <Spin size="large" />
      ) : (
        <Button type="primary" htmlType="submit">
          {isEdit ? 'Update' : 'Create'}
        </Button>
      )}
    </Form>
  );
};

export default CreateEditInfluencerCategory;
